import { useEffect, useRef, useState } from 'react';
import { ServiceRegistry } from '@/services/ServiceRegistry';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30; // ~60s ceiling — same as useTransactionSyncPolling.

export type SyncStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * A row's sync state, including the retry budget — `status` alone cannot distinguish a failure
 * the scheduler will retry from one it has permanently given up on, and those need opposite
 * treatment in the UI (FIN-745).
 */
export type SyncState = {
  status: SyncStatus;
  retryCount: number;
  maxRetries: number;
};

/**
 * Has the scheduler permanently stopped trying this account?
 *
 * `process-transaction-sync-jobs` skips an account once `retry_count >= max_retries`, so nothing
 * will ever move such a row out of 'failed' again. Before FIN-745 the row simply kept rendering
 * the "Importing…" spinner forever — confirmed live in staging with three rows at
 * retry_count = max_retries = 3 whose next_retry_at was days in the past.
 */
export function isSyncStopped(state: SyncState | undefined): boolean {
  return state !== undefined && state.status === 'failed' && state.retryCount >= state.maxRetries;
}

/**
 * Whether a bank-account row should still show its "importing" indication for this status
 * (bank-account-importing-indicator, FIN-568). Deliberately includes a 'failed' row that still
 * has retries left — bank-sync-first-fetch-context.AC13 requires the client never look "done" or
 * "confirmed empty" after an outright first-sync failure; it must keep reading as
 * still-in-progress while the existing retry/backoff mechanism (recordSyncFailure) works on it
 * in the background, per AC14's "engineers act fast, the client never notices" goal. `undefined`
 * (no cursor row at all) means genuinely never-attempted — that stays hidden, not shown.
 *
 * A failure whose retry budget is SPENT is excluded (FIN-745): AC13's premise is that retries are
 * still working on it, which stops being true once the scheduler has given up. That case is
 * terminal and belongs to isSyncStopped instead — otherwise the spinner never ends.
 */
export function isSyncStillImporting(state: SyncState | undefined): boolean {
  return state !== undefined && state.status !== 'completed' && !isSyncStopped(state);
}

/**
 * Batched, company-wide sync status for a per-account-row "importing" indicator (FIN-568 —
 * bank-account-importing-indicator).
 *
 * Unlike useTransactionSyncPolling (issue #363), which only starts polling in reaction to a
 * connect action that just happened in the same component instance, this hook checks real
 * persisted transaction_sync_state on every mount — so it's correct regardless of how the page
 * was reached, including navigating away while a sync is still running and coming back later.
 * One batched request covers every one of the company's bank accounts, not N parallel per-account
 * polls (approved trade-off — see specs/bank-sync-first-fetch-context/plan.md PR5 / FIN-568).
 *
 * `accountIds` is required so the hook can tell "no cursor row yet because the async sync
 * dispatch hasn't reached markSyncProcessing() yet" apart from "nothing to poll for" — a row's
 * absence is ambiguous on its own. A known account with no row yet keeps polling exactly like a
 * 'pending'/'processing' row would, up to the same ceiling. Confirmed live: without this, the
 * very first tick right after landing on /overview can race the async DB-trigger dispatch to
 * process-transaction-sync-jobs and win, causing the hook to see an empty response and stop
 * polling forever — the indicator never appears even though a sync genuinely starts moments
 * later.
 */
export function useCompanySyncStatus(accountIds: string[]) {
  const [statusByAccountId, setStatusByAccountId] = useState<Map<string, SyncState>>(new Map());
  const cancelledRef = useRef(false);
  const accountIdsRef = useRef(accountIds);
  accountIdsRef.current = accountIds;

  useEffect(() => {
    cancelledRef.current = false;
    const transactionService = ServiceRegistry.getTransactionService();
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = async () => {
      attempts += 1;

      try {
        const statuses = await transactionService.getSyncStatusForCompany();
        if (cancelledRef.current) return;

        const statusMap = new Map(statuses.map((s) => [s.bankAccountId, {
          status: s.status,
          retryCount: s.retryCount,
          maxRetries: s.maxRetries,
        }]));
        setStatusByAccountId(statusMap);

        const anyStillPending = accountIdsRef.current.some((id) => {
          const state = statusMap.get(id);
          return state === undefined || state.status === 'pending' || state.status === 'processing';
        });
        if (anyStillPending && attempts < MAX_ATTEMPTS) {
          timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        console.error('Failed to poll company sync status:', err);
      }
    };

    tick();

    return () => {
      cancelledRef.current = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-running per account-id-array
    // identity change would restart polling on every parent re-render; accountIdsRef carries the
    // latest value into the running tick loop instead.
  }, [accountIds.join(',')]);

  return { statusByAccountId };
}

export default useCompanySyncStatus;
